import { Component, NgZone, OnDestroy, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SnapshotService } from 'apps/account/src/account-shared/services/snapshot.service';
import { AccountSharedModule } from 'apps/account/src/account-shared';
import { LegacyShimsModule } from '../../../account-shared/legacy-shims/legacy-shims.module';
import { CoopNumberPipe, CoopPercentagePipe } from './cooperative.pipes';
import BigPictureActions from 'v2/app/features/cooperative/big-picture/actions/index';
import { CooperativeNgReduxFacade } from './store/cooperative-ngredux.facade';

/**
 * Cooperative "Big Picture" section: subscribes to the cooperative ngRedux store
 * (via the NgRx facade), dispatches the date-range thunk, and renders the advocate /
 * reach / lead / booking statistics as HTML stat tables and meters.
 */
@Component({
    selector: 'ft-cooperative-big-picture-ng',
    imports: [AccountSharedModule, LegacyShimsModule, CoopNumberPipe, CoopPercentagePipe],
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./cooperative-big-picture.component.scss'],
    templateUrl: './cooperative-big-picture.component.html',
})
export class CooperativeBigPictureComponent implements OnInit, OnDestroy {
    protected readonly propertiesStatistics = signal<any>(undefined);
    protected readonly startDate = signal<any>(undefined);
    protected readonly endDate = signal<any>(undefined);
    protected readonly isInitialized = signal(false);

    private readonly sessionService = inject(SessionService);
    private readonly spacesService = inject(SpacesService);
    // WS5 #10 inc 2r: native SnapshotService via a FIELD INITIALIZER (inject() in
    // ngOnInit throws NG0203). Same sessionStorage key + default as the v1 factory.
    private readonly snapshot = inject(SnapshotService);
    private readonly zone = inject(NgZone);
    private readonly advStore = inject(CooperativeNgReduxFacade);
    private $ngRedux: any;
    private company: any;
    private unsubscribe?: () => void;

    ngOnInit(): void {
        // WS2 strangle: cooperative state now in the native NgRx feature (facade).
        this.$ngRedux = this.advStore;
        const snapshot = this.snapshot;
        this.company = this.sessionService.getCompany(this.spacesService.getCompanyUuid());
        if (!this.company) return;

        const sync = () => {
            const state = this.$ngRedux.getState();
            const bp = state?.views?.cooperative?.bigPicture || {};
            this.zone.run(() => {
                this.propertiesStatistics.set(state?.apiData?.propertiesStatistics);
                this.startDate.set(bp.startDate);
                this.endDate.set(bp.endDate);
                this.isInitialized.set(bp.isInitialized);
            });
        };
        this.unsubscribe = this.$ngRedux.subscribe(sync);
        sync();
        this.$ngRedux.dispatch(BigPictureActions.dateRangeChange(this.company.UUID, new Date(snapshot.get().start), new Date(snapshot.get().end)));
    }

    protected onCalendarDateRangeChange(dateRange: any): void {
        this.$ngRedux.dispatch(BigPictureActions.dateRangeChange(this.company.UUID, dateRange.fromDate, dateRange.toDate));
    }

    /** Meter scale base = the first (largest) property's Total — v1 `Properties[0].Total`. */
    protected firstTotal(properties: any[]): number {
        return properties && properties.length ? properties[0].Total : 0;
    }

    ngOnDestroy(): void {
        this.unsubscribe?.();
    }
}
